// PricingRisk port — ports immersive_gui/ui/src/components/PricingRisk.jsx
// into Diletta. Section "Pricing & Risk" with five label/value rows:
//   Price   — sign-coloured (positive green, negative red)
//   Delta   — accent / soft-blue
//   Gamma   — text-bright (neutral)
//   Theta   — sign-coloured
//   Vega    — greek-vega (purple)
// Then a small mono model badge bottom-right. The risk-ladder trigger
// lives in the Section header's right slot — clicking it dispatches
// openLadder() on the provider.
import { FC, useContext, useEffect, useState } from 'react';
import { Box, IconButton, keyframes, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// MVP RiskBand.jsx swaps the ladder icon for a pulsing "ladder …" text
// while the background ladder fetch is in flight. Same keyframes
// (1s ease-in-out infinite, 30% min opacity).
const ladderPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
`;

import Number from '../../number';
import { greekColour } from '../exotics-shared';
import { ModelKind } from '../../../../pages/exotics-pricer/contracts';
import { PricingContext } from '../../../../providers/exotics-pricing-provider';
import { splitBaseQuote } from '../../../../providers/exotics-engine/engine-wire';

import './exotics-risk-band-styles.scss';

interface Props {
  instrumentId: string;
}

const MODEL_LABEL: Record<ModelKind, string> = {
  bs: 'BS (ATMF)',
  lv: 'Local Vol',
  sv: 'Stoch Vol',
  hybrid_lv_sv: 'Hybrid LV/SV',
};

// One row in the metric stack. Right-aligned numeric value with a
// chrome label on the left. The colour is computed in the caller so it
// can react to the value's sign.
const MetricRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack
    direction="row"
    alignItems="baseline"
    justifyContent="space-between"
    sx={{ py: 0, lineHeight: 1.5 }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: 10,
        letterSpacing: '0.06em',
        color: 'text.secondary',
        lineHeight: 'inherit',
      }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
);

// Small denomination tag shown after a metric's value — e.g. "USD" or
// "BTC" — so it's clear which token a greek is expressed in without
// having to remember the payoff-type convention below.
const UnitLabel: FC<{ unit: string }> = ({ unit }) => (
  <Typography
    component="span"
    sx={{
      fontSize: 9,
      letterSpacing: '0.04em',
      color: 'text.secondary',
      ml: 0.5,
    }}
  >
    {unit}
  </Typography>
);

const ExoticsRiskBandContent: FC<Props> = ({ instrumentId }) => {
  const { results, errors, instruments, openLadder, ladderLoading, loading } =
    useContext(PricingContext);
  const theme = useTheme();
  const r = results[instrumentId];
  const err = errors[instrumentId];
  const inst = instruments.find((i) => i.id === instrumentId);
  const isLadderLoading = !!ladderLoading[instrumentId];
  const isLoading = !!loading[instrumentId];

  // Denomination convention, applied uniformly across every contract type
  // (vanilla/barrier options and OneTouch/DoubleNoTouch bets alike):
  // Price/Theta/Vega are quote-denominated (premium/decay/vol-sensitivity
  // in cash terms), Delta/Gamma are base-denominated (underlying exposure).
  const { base, quote } = inst ? splitBaseQuote(inst.token) : { base: '', quote: '' };
  const priceThetaVegaUnit = quote;
  const deltaGammaUnit = base;

  // Hold the pulsing "ladder …" visible for at least 1000ms even when the
  // WS round-trip is faster than the animation cycle. Without this guard,
  // a 100-200ms calc never renders enough of a pulse to be noticeable.
  const [showLadderPulse, setShowLadderPulse] = useState(false);
  useEffect(() => {
    if (isLadderLoading) {
      setShowLadderPulse(true);
      return;
    }
    if (showLadderPulse) {
      const t = setTimeout(() => setShowLadderPulse(false), 1000);
      return () => clearTimeout(t);
    }
  }, [isLadderLoading, showLadderPulse]);

  const pvSign = r ? (r.pv >= 0 ? 'positive' : 'negative') : null;
  const thetaSign = r ? (r.theta >= 0 ? 'positive' : 'negative') : null;

  // MVP RiskBand pattern: while the background ladder fetch is in flight,
  // show pulsing "ladder …" text; otherwise show the chart-line icon
  // button.
  // Loading state is rendered as a plain Box (not an IconButton) so MUI's
  // disabled-button opacity doesn't fight the pulse animation.
  const ladderButton = showLadderPulse ? (
    <Box
      component="span"
      aria-label="risk-ladder-computing"
      sx={{
        display: 'inline-block',
        fontSize: 9,
        letterSpacing: '0.08em',
        lineHeight: 1,
        color: 'primary.light',
        px: 0.75,
        py: 0.5,
        animation: `${ladderPulse} 1s ease-in-out infinite`,
      }}
    >
      ladder …
    </Box>
  ) : (
    <Tooltip title="Open risk ladder">
      <span>
        <IconButton
          size="small"
          aria-label="open-risk-ladder"
          onClick={() => openLadder(instrumentId)}
          disabled={!r}
          sx={{
            p: 0.5,
            color: r ? 'primary.main' : 'text.disabled',
            '&:hover': { color: 'primary.light' },
          }}
        >
          <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 11 }} />
        </IconButton>
      </span>
    </Tooltip>
  );

  // Loading state moves to the page-level BandRow if needed; widget renders
  // the cell content directly.
  void isLoading;

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: 'rgba(40, 114, 226, 0.06)',
        border: '1px solid',
        borderColor: err ? 'error.main' : '#1f3645',
        borderRadius: '6px',
        px: 1.25,
        // Headroom above the Price row so the absolute-positioned ladder
        // button sits clear of the first metric line.
        pt: 3.5,
        pb: 0.75,
      }}
    >
      {/* ladder button — top-right of the cell */}
      <Box sx={{ position: 'absolute', top: 4, right: 6 }}>{ladderButton}</Box>
      {err ? (
        <Box sx={{ fontSize: 10, color: theme.palette.error.main, py: 1 }}>{err}</Box>
      ) : (
        <Stack spacing={0} className="exotics-risk-band-content">
          {/* MVP Pricing & Risk values are 11px (small role). */}
          <MetricRow label="Price">
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
              <Number
                value={r?.pv}
                format="price-2dp"
                role="small"
                colour={
                  pvSign === 'negative' ? 'negative' : pvSign === 'positive' ? 'positive' : 'muted'
                }
              />
              <UnitLabel unit={priceThetaVegaUnit} />
            </Box>
          </MetricRow>
          <MetricRow label="Delta">
            <Box
              component="span"
              sx={{
                color: greekColour(theme, 'delta'),
                display: 'inline-flex',
                alignItems: 'baseline',
              }}
            >
              <Number value={r?.delta} format="price-4dp" role="small" />
              <UnitLabel unit={deltaGammaUnit} />
            </Box>
          </MetricRow>
          <MetricRow label="Gamma">
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
              {/* Same precision as Delta (price-4dp) so the two rows align
                  visually rather than running at different dp widths. */}
              <Number value={r?.gamma} format="price-4dp" role="small" />
              <UnitLabel unit={deltaGammaUnit} />
            </Box>
          </MetricRow>
          <MetricRow label="Theta">
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
              <Number
                value={r?.theta}
                format="price-2dp"
                role="small"
                colour={
                  thetaSign === 'negative'
                    ? 'negative'
                    : thetaSign === 'positive'
                      ? 'positive'
                      : 'muted'
                }
              />
              <UnitLabel unit={priceThetaVegaUnit} />
            </Box>
          </MetricRow>
          <MetricRow label="Vega">
            <Box
              component="span"
              sx={{
                color: greekColour(theme, 'vega'),
                display: 'inline-flex',
                alignItems: 'baseline',
              }}
            >
              <Number value={r?.vega} format="price-2dp" role="small" />
              <UnitLabel unit={priceThetaVegaUnit} />
            </Box>
          </MetricRow>
          {inst && (
            <Typography
              sx={{
                fontSize: 9,
                fontFamily: 'Inter',
                fontVariantNumeric: 'tabular-nums',
                color: 'text.disabled',
                textAlign: 'right',
                pt: 0.5,
              }}
            >
              {MODEL_LABEL[inst.model]}
              {inst.model === 'hybrid_lv_sv' && inst.mixing != null
                ? ` (${inst.mixing.toFixed(2)})`
                : ''}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ExoticsRiskBandContent;
